import { useDerivationChat } from '../hooks/useDerivationChat';
import { DerivationStatus } from '../enums/DerivationStatus';
import { ChatInputArea } from './ChatInputArea';
import { FullChatView } from './FullChatView';
import { ChatSkeleton } from './ChatSkeleton';
import { ChatSummary } from './ChatSummary';
import React from 'react';

interface DerivationChatProps {
  derivationId: string;
  description?: string;
  sender?: string;
  contentHeight?: string | number;
}

export const DerivationChat: React.FC<DerivationChatProps> = ({
  derivationId,
  description = '',
  sender = '',
  contentHeight = '100%',
}) => {
  const {
    derivation,
    isSending,
    newMessage,
    setNewMessage,
    sendMessage,
    messagesEndRef,
    isLoadingDerivation,
    isInitialScrollDone,
    parsedData,
    showResumen,
    toggleResumen,
  } = useDerivationChat(derivationId, description, sender);

  const showSkeleton =
    isLoadingDerivation || !derivation || !isInitialScrollDone;
  const isAssigned = derivation?.status === DerivationStatus.ASSIGNED;
  const currentDescription = derivation?.description || description;

  return (
    <div
      className='flex flex-col w-full bg-gray-50 overflow-hidden relative'
      style={{ height: contentHeight }}
    >
      {showSkeleton && (
        <div className='absolute inset-0 z-50 bg-gray-50'>
          <ChatSkeleton />
        </div>
      )}

      <div
        className={`flex flex-col h-full overflow-hidden ${showSkeleton ? 'invisible' : 'visible'}`}
      >
        <div className='flex-grow min-h-0 overflow-y-auto overflow-x-hidden p-4 space-y-6 bg-gray-50 scroll-smooth shadow-inner'>
          <ChatSummary
            parsedData={parsedData}
            showResumen={showResumen}
            onToggle={toggleResumen}
          />

          <div className='flex-grow space-y-3 pb-4 overflow-hidden w-full'>
            <h3 className='text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100 pb-1 mb-6 text-center'>
              HISTORIAL DE CONVERSACIÓN
            </h3>
            <FullChatView
              chatData={currentDescription}
              conversation={derivation?.conversation}
            />
            <div ref={messagesEndRef} className='h-4' />
          </div>
        </div>

        <div
          className={`flex-shrink-0 transition-all duration-300 border-t border-gray-200 ${isAssigned ? 'h-24' : 'h-12'} bg-white overflow-hidden`}
        >
          {isAssigned ? (
            <ChatInputArea
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              sendMessage={sendMessage}
              isSending={isSending}
            />
          ) : (
            <div className='h-full flex items-center justify-center bg-gray-50/50'>
              <p className='text-xs text-gray-500 font-medium flex items-center gap-2'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={2}
                  stroke='currentColor'
                  className='w-4 h-4 text-orange-400'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z'
                  />
                </svg>
                Debes tomar la derivación para poder enviar mensajes
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
